import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { apiMessage } from './LabBenchErrors';
import { LabBenchTestPicker, splitOrderables, type LabBenchOrderable } from './LabBenchTestPicker';

interface AddTestsResponse {
  added: number;
  itemIds: string[];
  billedPanelIds: string[];
  skippedPanelIds: string[];
  invoiceId: string | null;
  newInvoice: boolean;
}

function billingNotice(result: AddTestsResponse): string {
  const billing = !result.invoiceId
    ? 'No bill line was raised for them (nothing priced, or this order is not billed here).'
    : result.newInvoice
      ? 'A new invoice was raised for the extra tests.'
      : 'The extra tests were added to the same invoice.';
  const skipped = result.skippedPanelIds.length;
  const overlap =
    skipped > 0
      ? ` ${skipped} overlapping panel${skipped === 1 ? ' was' : 's were'} not charged again because its tests were already on the order.`
      : '';
  return `${billing}${overlap}`;
}

export function LabBenchAddTestsDialog({
  orderId,
  orderNumber,
  patientName,
  onClose,
  onAdded,
}: {
  orderId: string;
  orderNumber: string;
  patientName: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const [chosen, setChosen] = useState<LabBenchOrderable[]>([]);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => post<AddTestsResponse>(`/lab/orders/${orderId}/add-tests`, splitOrderables(chosen)),
    onSuccess: (result) => {
      toast.success(`${result.added} test${result.added === 1 ? '' : 's'} added to ${orderNumber}`, billingNotice(result));
      onAdded();
    },
    onError: (caught) => setError(apiMessage(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'add_tests_x', { orderNumber: orderNumber })}
      description={tr('lab', 'x_the_difference_is_billed_to_this_patient', { patientName: patientName })}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              add.mutate();
            }}
            loading={add.isPending}
            disabled={chosen.length === 0}
          >
            {tr('lab', 'add')} {chosen.length} {tr('lab', 'item')}{chosen.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <LabBenchTestPicker chosen={chosen} onChange={setChosen} inputId="lab-add-tests" />
      </div>
    </Dialog>
  );
}
